// ============================================================================
// KpiSplitChips — Personal Finance V2 (Blueprint §9 / Fase 4)
// Chips clicáveis PAGO / ABERTO / PREVISÃO + indicador DIFF + Health Score.
// Substitui o rodapé "Saldo do Período" — agora no header.
// ============================================================================

import React from 'react';

const fmt = (val) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val || 0);

/**
 * @param {object} summary       — { income, expense, balance, byGroup }
 * @param {object} health        — { score, orphans, completePairs, singles }
 * @param {string} statusFilter  — null | 'PAID' | 'PENDING' | 'FORECAST'
 * @param {function} onFilterChange — (newFilter) => void
 * @param {number} totalPaid     — soma de todos os pagos
 * @param {number} totalPending  — soma de todos os em aberto
 * @param {number} totalForecast — soma de todas as previsões
 * @param {number} countAll      — total de lançamentos
 */
export function KpiSplitChips({
    summary,
    health,
    statusFilter,
    onFilterChange,
    totalPaid = 0,
    totalPending = 0,
    totalForecast = 0,
    countAll = 0,
}) {
    const diff = summary.income - summary.expense;
    const diffOk = Math.abs(diff) < 0.01;

    const chips = [
        {
            id: 'PAID',
            label: 'Pago',
            value: totalPaid,
            icon: 'fas fa-check-circle',
            active: 'bg-[#059669] text-white border-[#059669]',
            idle: 'bg-[#ecfdf5] text-[#059669] border-[#a7f3d0] hover:bg-[#d1fae5]',
            dot: 'bg-[#059669]',
        },
        {
            id: 'PENDING',
            label: 'Em Aberto',
            value: totalPending,
            icon: 'fas fa-clock',
            active: 'bg-[#d97706] text-white border-[#d97706]',
            idle: 'bg-[#fffbeb] text-[#d97706] border-[#fde68a] hover:bg-[#fef3c7]',
            dot: 'bg-[#d97706]',
        },
        {
            id: 'FORECAST',
            label: 'Previsão',
            value: totalForecast,
            icon: 'fas fa-calendar-alt',
            active: 'bg-[#7c3aed] text-white border-[#7c3aed]',
            idle: 'bg-[#f5f3ff] text-[#7c3aed] border-[#ddd6fe] hover:bg-[#ede9fe]',
            dot: 'bg-[#7c3aed]',
        },
    ];

    return (
        <div className="bg-white border border-[#d1d5db] px-3 py-2">
            <div className="flex items-center gap-3 flex-wrap">
                {/* Label contextual */}
                <span className="text-[9px] font-bold text-[#6b7280] uppercase tracking-wider border-r border-[#e5e7eb] pr-3">
                    <i className="fas fa-user mr-1"></i>Saídas PF
                </span>
                {/* Status filter chips */}
                {chips.map(c => {
                    const isActive = statusFilter === c.id;
                    return (
                        <button
                            key={c.id}
                            onClick={() => onFilterChange(isActive ? null : c.id)}
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 border font-bold text-[10px] transition-all ${
                                isActive ? c.active : c.idle
                            }`}
                            title={isActive ? 'Clique para remover filtro' : `Filtrar por ${c.label}`}
                        >
                            <i className={`${c.icon} text-[9px]`}></i>
                            <span>{c.label}</span>
                            <span className="font-mono tabular-nums">{fmt(c.value)}</span>
                        </button>
                    );
                })}

                {/* Divider */}
                <div className="w-px h-5 bg-[#d1d5db]"></div>

                {/* DIFF indicator */}
                <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-bold border ${
                    diffOk
                        ? 'bg-[#ecfdf5] text-[#059669] border-[#a7f3d0]'
                        : 'bg-[#fef2f2] text-[#dc2626] border-[#fecaca]'
                }`}>
                    <i className={`fas ${diffOk ? 'fa-balance-scale' : 'fa-exclamation-triangle'} text-[9px]`}></i>
                    <span>DIFF</span>
                    <span className="font-mono tabular-nums">{fmt(diff)}</span>
                    {diffOk && <i className="fas fa-check text-[8px]"></i>}
                </div>

                {/* Health Score */}
                {health && (
                    <>
                        <div className="w-px h-5 bg-[#d1d5db]"></div>
                        <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-bold border ${
                            health.score === 100
                                ? 'bg-[#ecfdf5] text-[#059669] border-[#a7f3d0]'
                                : health.score >= 80
                                    ? 'bg-[#fffbeb] text-[#d97706] border-[#fde68a]'
                                    : 'bg-[#fef2f2] text-[#dc2626] border-[#fecaca]'
                        }`}>
                            <i className={`fas ${health.score === 100 ? 'fa-shield-alt' : 'fa-exclamation-triangle'} text-[9px]`}></i>
                            <span>Saúde</span>
                            <span className="font-mono tabular-nums">{health.score}%</span>
                            {health.orphans > 0 && (
                                <span className="text-[8px] font-normal opacity-80">({health.orphans} órfão{health.orphans > 1 ? 's' : ''})</span>
                            )}
                        </div>
                    </>
                )}

                {/* Total + count */}
                <div className="flex-1"></div>
                <div className="text-[10px] text-[#6b7280] font-semibold tabular-nums">
                    {countAll} lançamento{countAll !== 1 ? 's' : ''}
                    {statusFilter && <span className="ml-1 text-[#C5A059]">· FILTRO: {statusFilter}</span>}
                </div>
            </div>
        </div>
    );
}
